import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../lib/api'
import { Button } from '../components/ui/Button'
import { Card, CardContent } from '../components/ui/Card'
import { ConfirmModal, AlertModal } from '../components/ui/Modal'
import type { Order, TimeSlot } from '../lib/types'
import { RECIPIENT_TYPE_LABELS, YEAR_GROUP_LABELS } from '../lib/types'

export default function OrdersPage() {
  const navigate = useNavigate()
  const [orders, setOrders] = useState<Order[]>([])
  const [slots, setSlots] = useState<TimeSlot[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [paymentLinks, setPaymentLinks] = useState<Record<string, string>>({})
  const [showOnlyPaymentIssues, setShowOnlyPaymentIssues] = useState(false)
  const [showPaymentPortalLink, setShowPaymentPortalLink] = useState<Record<string, boolean>>({})
  const [searchTerm, setSearchTerm] = useState('')
  const [isSearching, setIsSearching] = useState(false)
  const [loadError, setLoadError] = useState(false)
  const [copiedOrderId, setCopiedOrderId] = useState<string | null>(null)

  // Modal states
  const [showPaymentLinkModal, setShowPaymentLinkModal] = useState(false)
  const [currentPaymentLink, setCurrentPaymentLink] = useState('')
  const [linkCopied, setLinkCopied] = useState(true)
  const [showMarkAsPaidModal, setShowMarkAsPaidModal] = useState(false)
  const [currentOrderForPaid, setCurrentOrderForPaid] = useState<{ id: string; amount: number } | null>(null)
  const [markingAsPaid, setMarkingAsPaid] = useState(false)
  const [reconciling, setReconciling] = useState(false)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')
  const [isError, setIsError] = useState(false)

  // isLoading (full-page) is only for the first load; later refreshes update in place
  const loadOrders = useCallback(async (search?: string) => {
    if (search) setIsSearching(true)
    try {
      const [ordersData, slotsData] = await Promise.all([
        api.getAdminOrders(search),
        api.getAdminSlots(),
      ])
      setOrders(ordersData)
      setSlots(slotsData)
      setLoadError(false)
    } catch (error) {
      console.error('Failed to load orders:', error)
      setLoadError(true)
    } finally {
      setIsLoading(false)
      setIsSearching(false)
    }
  }, [])

  // Initial load and debounced search in one effect (immediate when the search box is empty)
  useEffect(() => {
    const timer = setTimeout(() => {
      loadOrders(searchTerm)
    }, searchTerm ? 300 : 0)
    return () => clearTimeout(timer)
  }, [searchTerm, loadOrders])

  const handleCreatePaymentLink = async (orderId: string) => {
    // Generate the evergreen payment portal link
    const siteUrl = window.location.origin
    const paymentPortalLink = `${siteUrl}/pay/${orderId}`

    // Copy to clipboard; the link is still shown below if the browser refuses
    let copied = true
    try {
      await navigator.clipboard.writeText(paymentPortalLink)
    } catch {
      copied = false
    }

    // Show the link in the UI
    setShowPaymentPortalLink(prev => ({ ...prev, [orderId]: true }))
    setPaymentLinks(prev => ({ ...prev, [orderId]: paymentPortalLink }))

    // Show modal
    setCurrentPaymentLink(paymentPortalLink)
    setLinkCopied(copied)
    setShowPaymentLinkModal(true)
  }

  const copyLink = async (orderId: string) => {
    try {
      await navigator.clipboard.writeText(paymentLinks[orderId])
      setCopiedOrderId(orderId)
      setTimeout(() => setCopiedOrderId(null), 2000)
    } catch {
      setCopiedOrderId(null)
    }
  }

  const handleMarkAsPaid = (orderId: string, orderAmount: number) => {
    setCurrentOrderForPaid({ id: orderId, amount: orderAmount })
    setShowMarkAsPaidModal(true)
  }

  const confirmMarkAsPaid = async () => {
    if (!currentOrderForPaid) return

    setMarkingAsPaid(true)
    try {
      const result = await api.markOrderPaid(currentOrderForPaid.id)

      // Refresh orders to show updated data
      await loadOrders()

      // Show success modal
      setShowMarkAsPaidModal(false)
      setIsError(false)
      setSuccessMessage(`Order marked as PAID\n\nReceipt ID: ${result.receiptId}\n\nThe order is now properly marked as paid and the warning will disappear.`)
      setShowSuccessModal(true)
    } catch (error: any) {
      console.error('Failed to mark order as paid:', error)
      setShowMarkAsPaidModal(false)
      setIsError(true)
      setSuccessMessage(`Failed to mark order as paid:\n\n${error.message || 'Please try again.'}`)
      setShowSuccessModal(true)
    } finally {
      setMarkingAsPaid(false)
      setCurrentOrderForPaid(null)
    }
  }

  const handleReconcile = async () => {
    setReconciling(true)
    try {
      const r = await api.reconcileOrders()
      await loadOrders()
      setIsError(r.errors.length > 0)
      setSuccessMessage(`Checked ${r.checked} unsettled checkout${r.checked === 1 ? '' : 's'} with SumUp.\n${r.completed} order${r.completed === 1 ? '' : 's'} confirmed as paid.${r.errors.length ? `\n\nErrors:\n${r.errors.join('\n')}` : ''}`)
    } catch (error: any) {
      setIsError(true)
      setSuccessMessage(`Reconcile failed:\n\n${error.message || 'Please try again.'}`)
    } finally {
      setReconciling(false)
      setShowSuccessModal(true)
    }
  }

  const exportAllOrders = () => {
    const headers = ['Order #', 'Order ID', 'Receipt ID', 'SumUp Ref', 'Name', 'Email', 'Total', 'Gifts Count', 'Status', 'Created']
    const rows = orders.map(order => [
      order.orderNumber ? String(order.orderNumber).padStart(3, '0') : 'Pending',
      order.id,
      order.receiptId || 'N/A',
      order.paymentId || 'N/A',
      order.fullName,
      order.email,
      `£${order.totalAmount.toFixed(2)}`,
      order.gifts?.length || 0,
      order.status,
      new Date(order.createdAt).toLocaleString(),
    ])

    const csv = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n')

    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `orders-${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  // Calculate statistics from completed orders only
  const completedOrders = orders.filter(order => order.status === 'completed')

  const exportTokens = () => {
    const ordersWithTokens = completedOrders.filter(order => order.tokenQuantity > 0)
    const headers = ['Name', 'Email', 'Token Count']
    const rows = ordersWithTokens.map(order => [
      order.fullName,
      order.email,
      order.tokenQuantity,
    ])

    const csv = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n')

    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `tokens-${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const printOrderList = () => {
    // Filter out foodbank gifts from the count - only count actual gifts for pickup
    const ordersWithGifts = completedOrders
      .map(order => ({
        ...order,
        pickupGifts: order.gifts?.filter(gift => gift.recipientType !== 'foodbank') || []
      }))
      .filter(order => order.pickupGifts.length > 0)
    const sortedOrders = [...ordersWithGifts].sort((a, b) => a.fullName.localeCompare(b.fullName))

    const printWindow = window.open('', '_blank')
    if (!printWindow) return

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Order List</title>
        <style>
          @page {
            size: A4;
            margin: 15mm 20mm;
          }
          * {
            box-sizing: border-box;
          }
          body {
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 20px 40px;
          }
          h1 {
            text-align: center;
            font-size: 28px;
            margin-bottom: 20px;
            border-bottom: 3px solid #333;
            padding-bottom: 10px;
          }
          .order-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 12px;
          }
          .order-item {
            border: 2px solid #333;
            border-radius: 8px;
            padding: 12px;
            display: flex;
            align-items: center;
            gap: 12px;
            page-break-inside: avoid;
          }
          .order-number {
            font-size: 36px;
            font-weight: bold;
            background: #333;
            color: white;
            width: 70px;
            height: 70px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 8px;
            flex-shrink: 0;
          }
          .order-details {
            flex: 1;
            min-width: 0;
          }
          .order-name {
            font-size: 18px;
            font-weight: bold;
            margin-bottom: 4px;
            word-wrap: break-word;
          }
          .order-gifts {
            font-size: 18px;
            color: #666;
          }
          @media print {
            body {
              padding: 0;
            }
          }
        </style>
      </head>
      <body>
        <h1>Order Pickup List</h1>
        <div class="order-grid">
          ${sortedOrders.map(order => `
            <div class="order-item">
              <div class="order-number">${order.orderNumber ? String(order.orderNumber).padStart(3, '0') : '---'}</div>
              <div class="order-details">
                <div class="order-name">${order.fullName}</div>
                <div class="order-gifts">${order.pickupGifts.length} gift${order.pickupGifts.length !== 1 ? 's' : ''}</div>
              </div>
            </div>
          `).join('')}
        </div>
      </body>
      </html>
    `

    printWindow.document.write(html)
    printWindow.document.close()
    printWindow.onload = () => {
      printWindow.print()
    }
  }

  // Identify orders with payment issues (completed but no receipt ID)
  const ordersWithoutReceipt = completedOrders.filter(order => !order.receiptId)

  // Filter orders based on showOnlyPaymentIssues
  const filteredOrders = showOnlyPaymentIssues
    ? ordersWithoutReceipt
    : orders

  const giftsByType = completedOrders.reduce((acc, order) => {
    order.gifts?.forEach(gift => {
      acc[gift.recipientType] = (acc[gift.recipientType] || 0) + 1
    })
    return acc
  }, {} as Record<string, number>)

  const totalTokens = completedOrders.reduce((sum, order) => sum + order.tokenQuantity, 0)

  const santaSlotsBooked = completedOrders.filter(order => order.santaBooking).length
  const totalSlots = slots.length
  const availableSlots = totalSlots - santaSlotsBooked

  const totalRevenue = completedOrders.reduce((sum, order) => sum + order.totalAmount, 0)
  const potentiallyUnpaidRevenue = ordersWithoutReceipt.reduce((sum, order) => sum + order.totalAmount, 0)

  if (isLoading) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Loading orders...</p>
      </div>
    )
  }

  if (loadError) {
    return (
      <div role="alert" className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex flex-wrap items-center gap-3">
        <span>Could not load orders. Check your connection and try again.</span>
        <button onClick={() => loadOrders(searchTerm)} className="min-h-[44px] px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 font-medium">
          Retry
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-2xl font-bold text-gray-900">All Orders</h1>
        <div className="flex flex-wrap gap-2">
          <Button onClick={handleReconcile} variant="outline" disabled={reconciling}>
            {reconciling ? 'Checking SumUp...' : 'Reconcile Payments'}
          </Button>
          <Button onClick={printOrderList} variant="outline">
            Print Order Poster
          </Button>
          <Button onClick={exportTokens} variant="outline">
            Export Tokens
          </Button>
          <Button onClick={exportAllOrders} variant="outline">
            Export All Orders
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <input
          type="text"
          placeholder="Search by name or email..."
          aria-label="Search orders by name or email"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
        <svg
          aria-hidden="true"
          className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        {isSearching && (
          <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-sm text-gray-500">
            Searching...
          </span>
        )}
      </div>

      {/* Payment Issues Warning */}
      {ordersWithoutReceipt.length > 0 && (
        <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4">
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-red-900 mb-1">
              Payment Verification Issues Detected
            </h3>
            <p className="text-sm text-red-800 mb-2">
              {ordersWithoutReceipt.length} order{ordersWithoutReceipt.length !== 1 ? 's are' : ' is'} marked as completed but missing receipt IDs (£{potentiallyUnpaidRevenue.toFixed(2)} total).
              These orders may have failed payments that were not properly verified.
            </p>
            <div className="flex items-center gap-3">
              <p className="text-xs text-red-700">
                Orders with payment issues are highlighted in red below.
              </p>
              <button
                onClick={() => setShowOnlyPaymentIssues(!showOnlyPaymentIssues)}
                className="min-h-[44px] text-xs font-medium text-red-900 bg-red-200 px-3 py-1.5 rounded hover:bg-red-300 transition-colors"
              >
                {showOnlyPaymentIssues ? 'Show All Orders' : 'Show Only Payment Issues'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Overview Statistics */}
      <Card variant="bordered">
        <CardContent>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Overview</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Total Orders */}
            <div className="space-y-1">
              <p className="text-sm text-gray-600">Completed Orders</p>
              <p className="text-2xl font-bold text-gray-900">{completedOrders.length}</p>
            </div>

            {/* Total Revenue */}
            <div className="space-y-1">
              <p className="text-sm text-gray-600">Total Revenue</p>
              <p className="text-2xl font-bold text-gray-900">£{totalRevenue.toFixed(2)}</p>
            </div>

            {/* Tokens Sold */}
            <div className="space-y-1">
              <p className="text-sm text-gray-600">Tokens Sold</p>
              <p className="text-2xl font-bold text-gray-900">{totalTokens}</p>
            </div>

            {/* Santa Slots */}
            <div className="space-y-1">
              <p className="text-sm text-gray-600">Santa Slots</p>
              <p className="text-2xl font-bold text-gray-900">
                {santaSlotsBooked} / {totalSlots}
              </p>
              <p className="text-xs text-gray-500">{availableSlots} available</p>
            </div>
          </div>

          {/* Gifts Breakdown */}
          {Object.keys(giftsByType).length > 0 && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h4 className="text-sm font-semibold text-gray-700 mb-3">Gifts by Type</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {Object.entries(giftsByType)
                  .sort(([, a], [, b]) => b - a)
                  .map(([type, count]) => (
                    <div key={type} className="flex justify-between items-center text-sm border border-gray-200 rounded-lg px-3 py-2 bg-gray-50">
                      <span className="text-gray-600">{RECIPIENT_TYPE_LABELS[type as keyof typeof RECIPIENT_TYPE_LABELS]}</span>
                      <span className="font-bold text-lg text-gray-900 ml-2">{count}</span>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {filteredOrders.length === 0 ? (
        <Card>
          <CardContent>
            <p className="text-center text-gray-600 py-8">
              {showOnlyPaymentIssues ? 'No orders with payment issues' : 'No orders yet'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredOrders.map((order) => {
            const hasPaymentIssue = order.status === 'completed' && !order.receiptId
            const isPending = order.status === 'pending_payment'

            return (
              <Card
                key={order.id}
                variant="bordered"
                className={hasPaymentIssue ? 'border-red-400 border-2 bg-red-50' : isPending ? 'border-yellow-400 border-2 bg-yellow-50' : ''}
              >
                <CardContent>
                  <div className="space-y-3">
                    {isPending && (
                      <div className="bg-yellow-100 border border-yellow-400 rounded-md p-3 mb-2">
                        <p className="text-sm font-semibold text-yellow-900">Payment Pending</p>
                        <p className="text-xs text-yellow-800 mt-0.5">
                          Checkout started but SumUp has not confirmed payment. Use "Reconcile Payments" to re-check, or wait for the customer to finish paying.
                        </p>
                      </div>
                    )}
                    {/* Payment Issue Banner */}
                    {hasPaymentIssue && (
                      <div className="bg-red-100 border border-red-400 rounded-md p-3 mb-2">
                        <div className="flex items-start gap-2">
                          <svg aria-hidden="true" className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <div className="flex-1">
                            <p className="text-sm font-semibold text-red-900">Payment Issue Detected</p>
                            <p className="text-xs text-red-700 mt-0.5">
                              Missing receipt ID - payment may have failed. Create a new payment link below to send to the customer.
                            </p>
                            {showPaymentPortalLink[order.id] ? (
                              <div className="mt-2 bg-white border border-green-300 rounded px-2 py-1.5">
                                <p className="text-xs font-semibold text-green-900 mb-1">
                                  Evergreen Payment Link (Never Expires):
                                </p>
                                <div className="flex items-center gap-2">
                                  <input
                                    type="text"
                                    value={paymentLinks[order.id]}
                                    readOnly
                                    aria-label="Payment link"
                                    className="flex-1 text-xs font-mono bg-gray-50 px-2 py-1 rounded border border-gray-300"
                                  />
                                  <button
                                    onClick={() => copyLink(order.id)}
                                    className="min-h-[44px] px-3 py-1 bg-green-700 text-white text-xs rounded hover:bg-green-800"
                                  >
                                    {copiedOrderId === order.id ? 'Copied' : 'Copy'}
                                  </button>
                                  <span role="status" className="sr-only">{copiedOrderId === order.id ? 'Link copied' : ''}</span>
                                </div>
                                <p className="text-xs text-green-700 mt-1">
                                  Customer can use this link anytime - it generates fresh checkout on each visit!
                                </p>
                              </div>
                            ) : (
                              <div className="mt-2 flex gap-2">
                                <button
                                  onClick={() => handleCreatePaymentLink(order.id)}
                                  className="flex-1 min-h-[44px] px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded hover:bg-blue-700"
                                >
                                  Generate Payment Portal Link
                                </button>
                                <button
                                  onClick={() => handleMarkAsPaid(order.id, order.totalAmount)}
                                  className="min-h-[44px] px-3 py-1.5 bg-green-700 text-white text-xs font-medium rounded hover:bg-green-800 whitespace-nowrap"
                                  title="Mark as paid if customer paid by cash or alternative method"
                                >
                                  Mark as Paid
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="flex justify-between items-start">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-2xl font-bold ${hasPaymentIssue ? 'text-red-900' : 'text-gray-900'}`}>
                            {order.orderNumber ? String(order.orderNumber).padStart(3, '0') : '---'}
                          </span>
                          <h3 className={`font-semibold text-lg ${hasPaymentIssue ? 'text-red-900' : 'text-gray-900'}`}>
                            {order.fullName}
                          </h3>
                          {order.notes && (
                            <span title={order.notes} aria-label={`Notes: ${order.notes}`} role="img" className="text-blue-600">
                              <svg aria-hidden="true" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                              </svg>
                            </span>
                          )}
                          {hasPaymentIssue && (
                            <span className="px-2 py-0.5 bg-red-600 text-white text-xs font-bold rounded">
                              PAYMENT ISSUE
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600">{order.email}</p>
                        <div className="text-xs text-gray-500 mt-1 space-y-0.5">
                          <p>
                            Order ID: <span className="font-mono">{order.id}</span>
                          </p>
                          {order.receiptId ? (
                            <p>
                              Receipt ID: <span className="font-mono">{order.receiptId}</span>
                            </p>
                          ) : (
                            <p className="text-red-600 font-semibold">
                              Receipt ID: <span className="font-mono">MISSING</span>
                            </p>
                          )}
                          {order.paymentId && (
                            <p>
                              SumUp Ref: <span className="font-mono">{order.paymentId}</span>
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="text-right space-y-2">
                        <p className={`text-lg font-bold ${hasPaymentIssue ? 'text-red-900' : 'text-gray-900'}`}>
                          £{order.totalAmount.toFixed(2)}
                        </p>
                        <p className="text-xs text-gray-500">
                          {new Date(order.createdAt).toLocaleString('en-GB', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                        <button
                          onClick={() => navigate(`/admin/orders/${order.id}`)}
                          className={`min-h-[44px] px-4 py-2 text-white text-sm rounded transition-colors ${
                            hasPaymentIssue
                              ? 'bg-red-600 hover:bg-red-700'
                              : 'bg-blue-600 hover:bg-blue-700'
                          }`}
                        >
                          {hasPaymentIssue ? 'View Details' : 'Edit'}
                        </button>
                      </div>
                    </div>

                  {order.gifts && order.gifts.length > 0 && (
                    <div className="border-t border-gray-200 pt-3">
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">
                        Gifts ({order.gifts.length})
                      </h4>
                      <ul className="space-y-1 text-sm">
                        {order.gifts.map((gift) => (
                          <li key={gift.id} className="text-gray-600">
                            {gift.recipientType === 'foodbank' ? (
                              <>
                                <span className="font-medium">Foodbank Donation</span>
                                {' '}({RECIPIENT_TYPE_LABELS[gift.recipientType]})
                                {' '}- Pickup: {gift.pickupLocation}
                              </>
                            ) : (
                              <>
                                <span className="font-medium">{gift.recipientName}</span>
                                {' '}({RECIPIENT_TYPE_LABELS[gift.recipientType]}) from{' '}
                                <span className="font-medium">{gift.fromName}</span>
                                {' '}({YEAR_GROUP_LABELS[gift.yearGroup]})
                                {' '}- Pickup: {gift.pickupLocation}
                              </>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {order.santaBooking && (
                    <div className="border-t border-gray-200 pt-3">
                      <h4 className="text-sm font-semibold text-gray-700 mb-1">
                        Santa Visit
                      </h4>
                      <p className="text-sm text-gray-600">
                        {order.santaBooking.timeSlot?.time} - Slot {order.santaBooking.timeSlot?.slotNumber}
                      </p>
                      <p className="text-sm text-gray-600">
                        Children: {JSON.parse(order.santaBooking.childrenNames).join(', ')}
                      </p>
                      {order.santaBooking.dietaryRequirements && (
                        <p className="text-sm text-gray-600">
                          Dietary: {order.santaBooking.dietaryRequirements}
                        </p>
                      )}
                    </div>
                  )}

                  {order.tokenQuantity > 0 && (
                    <div className="border-t border-gray-200 pt-3">
                      <h4 className="text-sm font-semibold text-gray-700 mb-1">
                        Activity Tokens
                      </h4>
                      <p className="text-sm text-gray-600">
                        Tokens: {order.tokenQuantity}
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
            )
          })}
        </div>
      )}

      {/* Payment Link Created Modal */}
      <AlertModal
        isOpen={showPaymentLinkModal}
        onClose={() => setShowPaymentLinkModal(false)}
        title="Payment Portal Link Created"
        type="success"
        message={
          <div className="space-y-3">
            <p className="font-semibold">{linkCopied ? 'Link copied to clipboard!' : 'Could not copy automatically. Copy the link below.'}</p>
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <p className="text-xs font-semibold text-green-900 mb-1">Evergreen Link (Never Expires):</p>
              <p className="text-xs font-mono text-green-800 break-all">{currentPaymentLink}</p>
            </div>
            <div className="text-xs space-y-1">
              <p>This link never expires!</p>
              <p>Send it to the customer via email or SMS</p>
              <p>Each visit generates a fresh 30-minute checkout</p>
            </div>
          </div>
        }
      />

      {/* Mark as Paid Confirmation Modal */}
      <ConfirmModal
        isOpen={showMarkAsPaidModal}
        onClose={() => {
          setShowMarkAsPaidModal(false)
          setCurrentOrderForPaid(null)
        }}
        onConfirm={confirmMarkAsPaid}
        title="Mark Order as Paid?"
        message={
          currentOrderForPaid
            ? `Amount: £${currentOrderForPaid.amount.toFixed(2)}\n\nUse this if the customer paid by:\n• Cash\n• Bank transfer\n• Alternative payment method\n\nA manual receipt ID will be generated.`
            : ''
        }
        confirmText="Yes, Mark as Paid"
        cancelText="Cancel"
        confirmButtonClass="bg-green-600 hover:bg-green-700"
        loading={markingAsPaid}
      />

      {/* Success/Error Modal */}
      <AlertModal
        isOpen={showSuccessModal}
        onClose={() => {
          setShowSuccessModal(false)
          setSuccessMessage('')
        }}
        title={isError ? 'Error' : 'Success'}
        type={isError ? 'error' : 'success'}
        message={successMessage}
      />
    </div>
  )
}
